/**
 * LoopNest Platform Owner / Super Admin Override
 * 
 * This module provides a single source of truth for admin override logic.
 * ONLY applies to: 365ddevotional@gmail.com
 * All other users follow normal Stripe billing and subscription logic.
 */

export const PLATFORM_OWNER_EMAIL = '365ddevotional@gmail.com';

/**
 * Check if an email belongs to the platform owner.
 * This is the ONLY place this check should live.
 */
export function isPlatformOwnerEmail(email) {
  return email?.toLowerCase?.() === PLATFORM_OWNER_EMAIL?.toLowerCase();
}

/**
 * Check if a user profile has super admin flags set.
 * Checks both the email AND the database flags for double safety.
 */
export function isSuperAdmin(user, userProfile) {
  if (!user) return false;
  // Email-based check (works even before DB flags are set)
  if (isPlatformOwnerEmail(user?.email)) return true;
  // DB flag check
  if (userProfile?.is_super_admin === true) return true;
  if (userProfile?.is_platform_owner === true) return true;
  return false;
}

/**
 * Check if billing should be bypassed for this user.
 * Returns true ONLY for the platform owner.
 */
export function hasBillingOverride(user, userProfile) {
  if (!user) return false;
  if (isPlatformOwnerEmail(user?.email)) return true;
  if (userProfile?.billing_override === true) return true;
  return false;
}

/**
 * Get the effective subscription tier for a user.
 * Platform owner always gets 'enterprise' regardless of DB state.
 */
export function getEffectiveTier(user, userProfile, dbTier = 'free') {
  if (isSuperAdmin(user, userProfile)) return 'enterprise';
  return dbTier || 'free';
}

/**
 * Get the simulated/display tier for the platform owner.
 * Allows the owner to switch between plans in the UI for testing
 * without losing actual enterprise access.
 */
export function getSimulatedTier(simulatedTier, user, userProfile) {
  if (!isSuperAdmin(user, userProfile)) return null;
  return simulatedTier || 'enterprise';
}

/**
 * Check if a feature is accessible.
 * Super admin always has access to all features.
 */
export function canAccessFeature(featureKey, user, userProfile, tier) {
  if (isSuperAdmin(user, userProfile)) return true;
  return checkTierFeature(featureKey, tier);
}

/**
 * Standard tier-based feature check for normal users.
 */
function checkTierFeature(featureKey, tier) {
  const tierLevel = { free: 0, pro: 1, studio: 2, enterprise: 3 };
  const featureRequirements = {
    unlimited_ai: 1,
    all_creator_tools: 1,
    all_export_formats: 2,
    premium_styles: 2,
    caption_sync: 2,
    team_collaboration: 2,
    api_access: 3,
    white_label: 3,
    admin_dashboard: 3,
    custom_duration: 2,
    hd_export: 1,
    '4k_export': 2,
  };
  const required = featureRequirements?.[featureKey] ?? 0;
  return (tierLevel?.[tier] ?? 0) >= required;
}

/**
 * Get all subscription tiers the platform owner can simulate.
 */
export const ALL_TIERS = ['free', 'pro', 'studio', 'enterprise'];

/**
 * Platform Owner Mode label text
 */
export const PLATFORM_OWNER_LABEL = 'Platform Owner Mode';
